import { type ChangeEvent, memo, useCallback } from "react";
import { makeStyles } from "@mui/styles";
import {
  Checkbox as MaterialCheckbox,
  type CheckboxProps as MaterialCheckboxProps,
  createSvgIcon,
} from "@mui/material";
import { C_OUTLINE_VARIANT, C_PRIMARY_HOVER, C_SECONDARY_DISABLED } from "./icon/Colors";
import classNames from "classnames";

const EnabledIcon = createSvgIcon(
  <g id="Behaviour" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
    <g id="Selection---DEV" transform="translate(-100.000000, -208.000000)">
      <rect fill="#FFFFFF" x="0" y="0" width="1608" height="436" />
      <g id="Checkbox_Enabled" transform="translate(103.000000, 211.000000)" fill="#CED5DA">
        <path d="M0,0 L0,18 L18,18 L18,0 L0,0 Z M2,2 L16,2 L16,16 L2,16 L2,2 Z" id="Path" />
      </g>
    </g>
  </g>,
  "EnabledIcon",
);

const DisabledIcon = createSvgIcon(
  <g id="Behaviour" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
    <g id="Selection---DEV" transform="translate(-820.000000, -208.000000)">
      <rect fill="#FFFFFF" x="0" y="0" width="1608" height="436" />
      <g id="Checkbox_Disabled" transform="translate(823.000000, 211.000000)">
        <polygon id="Path" fill="#C1C0C0" points="0 0 0 18 18 18 18 0" />
      </g>
    </g>
  </g>,
  "DisabledIcon",
);

const CheckedIcon = createSvgIcon(
  <g id="Behaviour" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
    <g id="Selection---DEV" transform="translate(-820.000000, -208.000000)">
      <rect fill="#FFFFFF" x="0" y="0" width="1608" height="436" />
      <g id="Checkbox_Active" transform="translate(823.000000, 211.000000)">
        <polygon id="Path" fill="currentColor" points="0 0 0 18 18 18 18 0" />
        <g id="icon/icn_checkmark" transform="translate(3.600000, 3.600000)" fill="#FFFFFF">
          <polygon
            id="Path"
            points="3.43207266 7.97277181 0.871717904 5.39800671 0 6.26861074 3.43207266 9.72 10.8 2.31060403 9.93426854 1.44"
          />
        </g>
      </g>
    </g>
  </g>,
  "CheckedIcon",
);

const enabledIcon = <EnabledIcon />;
const disabledIcon = <DisabledIcon />;
const checkedIcon = <CheckedIcon />;

export interface CheckboxProps {
  className?: string;
  name?: string;
  checked?: boolean;
  id?: string;
  disabled?: boolean;
  onCheck?: (newVal: boolean) => void;
  onChange?: MaterialCheckboxProps["onChange"];
  onClick?: MaterialCheckboxProps["onClick"];
  "data-test-id"?: string;
  inputRef?: MaterialCheckboxProps["inputRef"];
  inputProps?: MaterialCheckboxProps["inputProps"];
  error?: boolean;
  indeterminate?: boolean;
}

const useStyles = makeStyles({
  rootDiv: {
    fontSize: "16px",
  },
  root: {
    margin: "-12px !important",
    padding: "12px !important",
    fontSize: "inherit",
    color: C_OUTLINE_VARIANT,
    "& svg": {
      fontSize: "inherit",
    },
    "& path": {
      transition: "fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    },
  },
  hover: {
    color: C_PRIMARY_HOVER + " !important",
  },
  disabled: {
    color: C_SECONDARY_DISABLED + " !important",
  },
});

const Checkbox = (props: CheckboxProps) => {
  const { className, onCheck, "data-test-id": testId, error, inputProps = {}, ...checkboxProps } = props;
  const onClick = useCallback(
    (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (onCheck) {
        onCheck(checked);
      }
    },
    [onCheck],
  );

  const { rootDiv, ...classes } = useStyles();

  return (
    <div className={classNames(className, rootDiv)}>
      <MaterialCheckbox
        onChange={onClick}
        classes={{
          root: classNames(classes.root),
          disabled: classes.disabled,
        }}
        color={error ? "error" : "primary"}
        {...checkboxProps}
        checkedIcon={checkboxProps.disabled ? disabledIcon : checkedIcon}
        icon={checkboxProps.disabled ? disabledIcon : enabledIcon}
        inputProps={{ "data-test-id": testId, ...inputProps }}
      />
    </div>
  );
};

export default memo(Checkbox);
